import type { ReportableScale } from "@qendo/profile/reports";
import { Mood, MoodIconMap, MoodMap, ScaleType } from "@qendo/trackables";
import { BaseText, type BaseTextProps, Subtitle } from "~/components/layout/text";
import { QendoIcon } from "~/components/QendoIcon";
import { LineHeights } from "~/constants/typography";
import { nullish } from "~/util";
import { formatAverage } from "~/util/qendo/reports";
import { type OptionalNumeric, parseNumber } from "~/util/string";

export type ReportAverageProps = {
    scale: ReportableScale;
    scale_name: string;
    average?: OptionalNumeric;
    averageProps?: Omit<BaseTextProps, "children">;
    subtitleProps?: Omit<BaseTextProps, "children">;
};

export const ReportAverage = ({ scale, scale_name, average, averageProps, subtitleProps }: ReportAverageProps) => {
    if (nullish(average)) {
        return <Subtitle {...subtitleProps}>no data</Subtitle>;
    }

    if (scale === ScaleType.mood) {
        const mood = Math.max(Mood.very_sad, Math.min(Mood.very_happy, Math.round(parseNumber(average)))) as Mood;

        return (
            <>
                <QendoIcon
                    name={MoodIconMap[mood]}
                    size={LineHeights[averageProps?.size ?? "base"]}
                    style={averageProps?.style}
                />
                <Subtitle {...subtitleProps}>{MoodMap[mood]} avg</Subtitle>
            </>
        );
    }

    const subtitle =
        (scale === ScaleType.water && "avg L") ||
        (scale === ScaleType.temperature && "avg °C") ||
        (scale === ScaleType.sleep && "avg hours") ||
        `avg ${scale_name.toLowerCase()}`;

    return (
        <>
            <BaseText {...averageProps}>{formatAverage(average)}</BaseText>
            <Subtitle {...subtitleProps}>{subtitle}</Subtitle>
        </>
    );
};
